import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, GraduationCap, Hotel, ShoppingBag, BarChart3, Wrench, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';
import './Solutions.css';

const INDUSTRIES = [
  {
    id: 'finance',
    icon: <BarChart3 size={28} />,
    name: 'Financial Services',
    headline: 'SEBI-Compliant Software for Modern Finance',
    desc: 'From Registrar & Transfer Agents to full-stack brokerage operations — SHIVKMS delivers battle-tested software built for the regulatory complexity of Indian financial markets.',
    challenge: 'Financial firms need software that keeps pace with SEBI rule changes, handles millions of transactions without errors, and maintains audit trails that satisfy regulators.',
    solution: 'Our RTA platform automates compliance workflows, integrates with depositories, processes bulk data, and generates regulatory reports automatically.',
    outcomes: [
      '99.99% transaction accuracy',
      'Automatic SEBI compliance updates',
      'Real-time dividend distribution',
      'Full investor grievance management',
    ],
    products: ['RTA Software', 'CRM Suite'],
    clients: ['Religare', 'SKI', 'Mindex'],
  },
  {
    id: 'education',
    icon: <GraduationCap size={28} />,
    name: 'Education',
    headline: 'Smart Campus Software That Actually Gets Used',
    desc: 'From small coaching institutes to universities — our education suite is designed for India\'s real classrooms: offline-capable, multilingual-ready, and built for scale.',
    challenge: 'Schools juggle hundreds of administrative tasks while staff struggle with tools that require constant IT support. Parents want real-time updates. Management wants data.',
    solution: 'A unified platform covering admissions, academics, fees, attendance, and parent communication — built for ease of use, not training time.',
    outcomes: [
      '60% reduction in admin workload',
      'Real-time parent engagement',
      'Automated fee reminders & collection',
      'Instant report card generation',
    ],
    products: ['School Management ERP', 'Mobile Apps'],
    clients: ['IWP Academy', 'Green Land Model School', 'Sports University of Haryana'],
  },
  {
    id: 'hospitality',
    icon: <Hotel size={28} />,
    name: 'Hospitality',
    headline: 'PMS That Runs as Smoothly as Your Service',
    desc: 'A property management system designed for India\'s hospitality operators — from heritage boutique hotels to corporate chains — with OTA integrations and revenue analytics.',
    challenge: 'Hotel operations span dozens of departments that rarely talk to each other. Manual check-ins, mismanaged housekeeping, and poor revenue visibility directly impact guest satisfaction.',
    solution: 'Our Hotel PMS connects front desk, housekeeping, F&B, and finance in real time — while connecting to Booking.com, Expedia, and direct booking engines.',
    outcomes: [
      '30% improvement in occupancy visibility',
      'Automated housekeeping task dispatch',
      'OTA sync in under 60 seconds',
      'Full night audit in one click',
    ],
    products: ['Hotel Management Software', 'CRM Suite'],
    clients: ['Valorelle', 'Indus Hotels'],
  },
  {
    id: 'retail',
    icon: <ShoppingBag size={28} />,
    name: 'Retail & E-Commerce',
    headline: 'From Physical Store to Digital Storefront',
    desc: 'Whether you\'re launching a D2C brand or scaling a multi-vendor marketplace — SHIVKMS e-commerce handles your catalog, inventory, payments, and customer relationships.',
    challenge: 'Retailers face growing pressure to sell online, manage inventory across channels, and deliver fast, reliable fulfilment — all with limited tech teams.',
    solution: 'Our e-commerce platform handles the entire commerce lifecycle: product listing, multi-channel inventory sync, payment processing, order tracking, and post-purchase experience.',
    outcomes: [
      'Multi-channel inventory sync',
      'UPI, Razorpay & COD support',
      'Automated abandoned cart recovery',
      'SEO-optimized product pages',
    ],
    products: ['E-Commerce Platform', 'CRM Suite'],
    clients: ['Sparepedia', 'Abhipra'],
  },
  {
    id: 'enterprise',
    icon: <Building2 size={28} />,
    name: 'Enterprise & SME',
    headline: 'One ERP to Run Your Entire Business',
    desc: 'Whether you\'re a 20-person SME or a 500-person enterprise, our CRM & ERP suite scales with you — without changing your workflows to fit the software.',
    challenge: 'Growing companies hit an operational ceiling when spreadsheets, WhatsApp messages, and fragmented tools can\'t keep up with business complexity.',
    solution: 'A unified ERP covering sales, HR, finance, inventory, and analytics — with custom workflow automation that learns your business processes.',
    outcomes: [
      'Single source of truth for all data',
      'Automated financial reconciliation',
      'Drag-and-drop workflow builder',
      'Real-time business intelligence',
    ],
    products: ['CRM & ERP Suite', 'Custom Mobile Apps'],
    clients: ['Maashitla', 'Sports University of Haryana'],
  },
  {
    id: 'construction',
    icon: <Wrench size={28} />,
    name: 'Construction',
    headline: 'Put Your Scaffolding on Software That Works at Height',
    desc: 'Scaffolding management built for India\'s construction sites — tracking materials, workers, safety inspections, and billing in one place.',
    challenge: 'Construction projects lose days to misplaced scaffold materials, missed inspections, and billing disputes. Paper-based tracking fails at scale.',
    solution: 'Our scaffolding platform digitizes material tracking, assigns tasks to field crews, automates inspection workflows, and generates OSHA-compliant reports.',
    outcomes: [
      'Zero material loss with RFID tagging',
      'Digital safety inspection checklist',
      'Real-time field crew tracking',
      'Automated billing & utilization reports',
    ],
    products: ['Scaffolding Solutions', 'Documentation Services'],
    clients: [],
  },
];

export default function Solutions() {
  const [active, setActive] = useState('finance');
  const current = INDUSTRIES.find(i => i.id === active);

  return (
    <div className="solutions-page">
      <SEO
        title="Industry Solutions — Finance, Education, Hospitality & More"
        description="See how SHIVKMS delivers tailored software solutions for financial services, education, hospitality, retail, enterprise and construction sectors across India."
        keywords="industry software solutions, fintech software India, education software, hospitality software, construction management software"
        path="/solutions"
      />
      {/* Hero */}
      <section className="solutions-hero">
        <div className="solutions-hero-bg" />
        <div className="container">
          <div className="solutions-hero-inner">
            <span className="eyebrow">Industry Solutions</span>
            <h1>Software That Speaks <span className="gradient-text">Your Industry's Language</span></h1>
            <p>Generic software forces you to change how you work. We build software around how your industry actually operates.</p>
          </div>
        </div>
      </section>

      {/* Industry Selector */}
      <section className="section-py">
        <div className="container">
          <div className="solutions-layout">
            {/* Sidebar */}
            <div className="solutions-sidebar">
              {INDUSTRIES.map(ind => (
                <button
                  key={ind.id}
                  className={`industry-btn ${active === ind.id ? 'active' : ''}`}
                  onClick={() => setActive(ind.id)}
                >
                  <span className="industry-btn-icon">{ind.icon}</span>
                  <span className="industry-btn-label">{ind.name}</span>
                  <ArrowRight size={16} className="industry-btn-arrow" />
                </button>
              ))}
            </div>

            {/* Content */}
            {current && (
              <div className="solutions-content" key={current.id}>
                <div className="sc-header">
                  <span className="eyebrow">{current.name}</span>
                  <h2>{current.headline}</h2>
                  <p className="sc-desc">{current.desc}</p>
                </div>

                <div className="sc-grid">
                  <div className="sc-card">
                    <h4 className="sc-card-title">The Challenge</h4>
                    <p>{current.challenge}</p>
                  </div>
                  <div className="sc-card sc-card-dark">
                    <h4 className="sc-card-title">Our Solution</h4>
                    <p>{current.solution}</p>
                  </div>
                </div>

                <div className="sc-outcomes">
                  <h4 className="sc-section-label">Measurable Outcomes</h4>
                  <div className="sc-outcomes-grid">
                    {current.outcomes.map((o, i) => (
                      <div key={i} className="sc-outcome">
                        <CheckCircle size={16} className="sc-check" />
                        <span>{o}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="sc-footer">
                  <div className="sc-products">
                    <span className="sc-footer-label">Relevant Products</span>
                    {current.products.map(p => (
                      <span key={p} className="sc-product-badge">{p}</span>
                    ))}
                  </div>
                  {current.clients.length > 0 && (
                    <div className="sc-clients">
                      <span className="sc-footer-label">Trusted By</span>
                      {current.clients.map(c => (
                        <span key={c} className="sc-client">{c}</span>
                      ))}
                    </div>
                  )}
                  <Link to="/contact" className="btn-primary sc-cta">
                    Get a Custom Demo <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="section-py approach-section">
        <div className="approach-bg" />
        <div className="container">
          <div className="section-heading">
            <span className="eyebrow">Our Process</span>
            <h2>How We <span className="gradient-text">Deliver Results</span></h2>
            <div className="divider" />
          </div>
          <div className="approach-steps">
            {[
              { n: '01', title: 'Discovery', desc: 'We spend time with your team to map current workflows, pain points, and desired outcomes before writing a single line of code.' },
              { n: '02', title: 'Architecture', desc: 'Our tech leads design a scalable, modular architecture tailored to your operational complexity and future growth needs.' },
              { n: '03', title: 'Build & Iterate', desc: 'Agile sprints with weekly demos. You see working software early and shape it with real feedback — not just a spec document.' },
              { n: '04', title: 'Deploy & Train', desc: 'Full onboarding programs, staff training, and data migration support ensure smooth go-live with minimal disruption.' },
              { n: '05', title: 'Support & Evolve', desc: '24/7 helpdesk, regular updates, and quarterly business reviews keep your software growing as your business grows.' },
            ].map((s, i) => (
              <div key={i} className="approach-step">
                <span className="approach-step-n">{s.n}</span>
                <h3 className="approach-step-title">{s.title}</h3>
                <p className="approach-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
